import { useState } from 'react';
import { useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTRPC } from '../../lib/trpc';
import { PageRoot } from '../ui/Page';
import { UnderlineTabPanel, UnderlineTabStrip, UnderlineTabs } from '../ui/Tabs';
import { CUSTOMER_TABS, useUrlTab } from '../ui/pageTabs';
import { BackCrumb, DetailHeader, MetaStrip, SectionCard } from '../ui/Detail';
import { StatusBadge } from '../ui/Badge';
import { dash } from '@/components/ui/data-table';
import { PageSkeleton } from '../ui/Skeleton';
import { EmptyState } from '../ui/EmptyState';
import { ErrorState, GoneState, isNotFound } from '../ui/QueryStates';
import { IconCustomers } from '../icons';
import { ContactsCard } from '../customers/ContactsCard';
import { NotesCard } from '../notes/NotesCard';
import { CustomerEquipmentCard } from '../equipment/CustomerEquipmentCard';
import { CustomFieldsCard } from '../customfields/CustomFieldsCard';
import { EditCustomerSheet } from '../customers/EditCustomerSheet';
import { EditPropertySheet } from '../customers/EditPropertySheet';
import { Button } from '../ui/Button';
import { can } from '@servwell/shared';
import { useSession } from '../session';

// Keys typed to the route's own list (pageTabs) — a label here cannot drift from what
// `/customers/$customerId`'s validateSearch admits.
const TABS: { key: (typeof CUSTOMER_TABS)[number]; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'equipment', label: 'Equipment' },
  { key: 'notes', label: 'Notes' },
];

export function CustomerDetailPage() {
  // `from` is the route ID — the pathless 'office' layout prefixes it (URL stays /customers/…).
  const { customerId } = useParams({ from: '/office/customers/$customerId' });
  const [tab, setTab] = useUrlTab(CUSTOMER_TABS);
  const trpc = useTRPC();
  const me = useSession();
  const q = useQuery(trpc.customer.get.queryOptions({ customerId }));
  const [editing, setEditing] = useState(false);
  /** Which address is open for editing — properties are a list, so this is an id not a flag. */
  const [editProperty, setEditProperty] = useState<string | null>(null);

  // The server returns null ONLY for a genuinely missing customer, so a failed or
  // paused query must never be reported as "deleted" — three distinct states.
  if (q.isPending) return <PageSkeleton />;
  if (q.isError) {
    return (
      <PageRoot>
        <BackCrumb to="/customers" label="Customers" />
        {/* Deleted, or an old link — gone is not broken, and there is nothing to retry. */}
        {isNotFound(q.error) ? (
          <GoneState
            icon={IconCustomers}
            what="customer"
            backTo="/customers"
            backLabel="Back to customers"
          />
        ) : (
          <ErrorState
            icon={IconCustomers}
            what="this customer"
            onRetry={() => void q.refetch()}
          />
        )}
      </PageRoot>
    );
  }
  const data = q.data;
  if (!data) {
    return (
      <PageRoot>
        <BackCrumb to="/customers" label="Customers" />
        <EmptyState
          icon={IconCustomers}
          title="Customer not found"
          description="It may have been deleted, or the link is stale."
        />
      </PageRoot>
    );
  }

  const { customer, properties } = data;

  return (
    <PageRoot>
      <BackCrumb to="/customers" label="Customers" />
      <DetailHeader
        title={customer.displayName}
        badge={
          <StatusBadge tone={customer.kind === 'commercial' ? 'brand' : 'neutral'}>
            {customer.kind}
          </StatusBadge>
        }
        right={
          can(me.role, 'customer', 'update') ? (
            <Button variant="ghost" onClick={() => setEditing(true)}>
              Edit
            </Button>
          ) : null
        }
      />
      <MetaStrip
        items={[
          { label: 'Email', value: dash(customer.email) },
          { label: 'Phone', value: dash(customer.phone) },
          {
            label: 'Terms',
            value:
              customer.defaultTermsDays !== null &&
              customer.defaultTermsDays !== undefined
                ? `Net ${customer.defaultTermsDays}`
                : '—',
          },
          { label: 'PO required', value: customer.poRequired ? 'Yes' : 'No' },
        ]}
      />
      {/* 105: the flat five-card grid went into tabs. Meta and the Edit door stay
          visible on every tab; content moved verbatim, nothing rewritten. */}
      <UnderlineTabs value={tab} onValueChange={setTab}>
        <UnderlineTabStrip tabs={TABS} />

        <UnderlineTabPanel keepMounted value="overview">
          <div className="grid gap-4 lg:grid-cols-2">
            <ContactsCard customerId={customerId} />
            <SectionCard title={`Properties (${properties.length})`}>
              {properties.length === 0 ? (
                <p className="text-sm text-muted-foreground">No properties yet.</p>
              ) : (
                <ul className="divide-y divide-border text-sm">
                  {properties.map((p) => (
                    <li
                      key={p.id}
                      className="flex items-start justify-between gap-2 py-2 first:pt-0 last:pb-0"
                    >
                      <div className="min-w-0">
                        <p className="font-medium">{p.label}</p>
                        <p className="text-xs text-muted-foreground">
                          {dash(
                            [p.address1, p.city, p.state, p.zip]
                              .filter(Boolean)
                              .join(', ') || null,
                          )}
                        </p>
                        {/* The gate code used to print here. It is no longer in THIS query:
                        the list runs on `customer.get`, which a bookkeeper may call, and
                        the code is a redline field. Owner and office see it by opening
                        Edit, which reads through `customer.propertyForEdit` — gated on
                        `property:update`, the one door that exists to serve these two
                        values.

                        A TECH has no path to it from this page — the Edit button needs
                        `property:update`, which they do not hold. That is the intended
                        shape, not an oversight: a tech gets the gate code for the stop
                        they were SENT to, on their own visit screen, and not for every
                        address in the shop from a list they can browse.

                        This page now holds no gate code at all, in any query: the sibling
                        `CustomerEquipmentCard` fetches `customer.properties`, and that read
                        lost the redlines too. It had to — `property:read` is a permission a
                        TECH holds for every address in the shop, so the value was sitting in
                        this page's query cache for a role that should only ever get it for a
                        stop they were sent to. */}
                      </div>
                      {can(me.role, 'property', 'update') ? (
                        <Button variant="ghost" onClick={() => setEditProperty(p.id)}>
                          Edit
                        </Button>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </SectionCard>
            <CustomFieldsCard entityType="customer" entityId={customerId} />
          </div>
        </UnderlineTabPanel>

        <UnderlineTabPanel keepMounted value="equipment">
          <CustomerEquipmentCard customerId={customerId} />
        </UnderlineTabPanel>

        <UnderlineTabPanel keepMounted value="notes">
          <NotesCard entityType="customer" entityId={customerId} />
        </UnderlineTabPanel>
      </UnderlineTabs>

      {/* Keyed on the row so reopening a different address starts from ITS values rather
          than the last one's — a sheet that keeps stale state is how you edit the wrong
          address without noticing. */}
      {/* Only the whole row prefills the edit: a tech's answer holds back the email and the
          billing (CRM 008), and a held-back email would save as cleared. This office page
          never gets one; the check is on the email itself. */}
      {editing && customer.email !== undefined ? (
        <EditCustomerSheet
          key={customer.id}
          open
          onClose={() => setEditing(false)}
          customer={customer}
        />
      ) : null}
      {editProperty ? (
        <EditPropertySheet
          key={editProperty}
          open
          onClose={() => setEditProperty(null)}
          propertyId={editProperty}
        />
      ) : null}
    </PageRoot>
  );
}
